import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import SidePanel from "../Sections/SidePanel/SidePanel";
import CalendarPage from "../Sections/CalendarPage/CalendarPage";
import ManageCalendarPage from "../Sections/Calendar/ManageCalendarPage";
import WorkboardsPage from "../Sections/WorkboardsPage/WorkboardsPage";
import EditorPage from "../Sections/Editor/EditorPage";
import NewRequestsPage from "../Sections/NewRequestsPage/NewRequestsPage";
import UserPermissionsPage from "../Sections/UserPermissionsPage/UserPermissionsPage";
import ReportingPage from "../Sections/ReportingPage/ReportingPage";
import TeamsAndBoardsPage from "../Sections/TeamsAndBoardsPage/TeamsAndBoardsPage";
import SetPasswordModal from "../components/SetPasswordModal";
import api from "../api";
import "./ReleaseAdminPage.css";

const ReleaseAdminPage = () => {
  const { isAuthenticated, user, needsPasswordSetup } = useAuth();
  const [activePage, setActivePage] = useState("new-requests"); // Default to public page
  const [resourceAccess, setResourceAccess] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load user's resource access when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      api.get('/auth/resource-access')
        .then(res => {
          setResourceAccess(res.data || []);
          setLoading(false);
        })
        .catch(err => {
          console.error('Failed to load resource access:', err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Check if user has access to a resource type (and optionally a specific resource ID)
  const hasAccess = (resourceType, requiredLevel = 'view', resourceId = null) => {
    if (!isAuthenticated) return false;
    
    const accessLevels = { view: 1, edit: 2, admin: 3 };
    const requiredLevelValue = accessLevels[requiredLevel];
    
    // First check for specific resource access (if resourceId provided)
    if (resourceId !== null) {
      const specificAccess = resourceAccess.find(a => 
        a.resource_type === resourceType && a.resource_id === resourceId
      );
      if (specificAccess) {
        const userLevelValue = accessLevels[specificAccess.access_level];
        return userLevelValue >= requiredLevelValue;
      }
    }
    
    // Then check for type-level access (resource_id is null)
    const typeAccess = resourceAccess.find(a => 
      a.resource_type === resourceType && (a.resource_id === null || a.resource_id === undefined)
    );
    if (typeAccess) {
      const userLevelValue = accessLevels[typeAccess.access_level];
      return userLevelValue >= requiredLevelValue;
    }
    
    return false;
  };

  // Check if user has access to ANY board (type-level or specific board)
  const hasAnyBoardAccess = () => {
    if (!isAuthenticated) return false;
    return resourceAccess.some(a => 
      a.resource_type === 'board' && 
      (a.resource_id === null || a.resource_id === undefined || a.resource_id !== null)
    );
  };

  // Handle page navigation with access check
  const handlePageChange = (page) => {
    // New Requests is always accessible (public)
    if (page === "new-requests") {
      setActivePage(page);
      return;
    }

    // Check access for other pages
    let hasPermission = false;
    switch (page) {
      case "calendar":
        hasPermission = hasAccess('calendar', 'view');
        break;
      case "manage-calendar":
        hasPermission = hasAccess('calendar-manage', 'view'); // Separate resource type
        break;
      case "workboards":
        // Allow access if user has access to ANY board (type-level or specific)
        hasPermission = hasAnyBoardAccess();
        break;
      case "editor":
        hasPermission = hasAccess('admin', 'view'); // Admin is a resource type, view is the level
        break;
      case "user-permissions":
        hasPermission = hasAccess('admin', 'edit'); // Requires edit access to admin resource
        break;
      case "reporting":
        hasPermission = hasAccess('admin', 'view'); // Requires view access to admin resource
        break;
      case "teams-boards":
        hasPermission = hasAccess('admin', 'edit'); // Requires edit access to admin resource
        break;
      default:
        hasPermission = false;
    }

    if (hasPermission) {
      setActivePage(page);
    } else {
      alert('You do not have access to this area. Please contact an administrator.');
    }
  };

  const renderPage = () => {
    // New Requests is always accessible
    if (activePage === "new-requests") {
      return <NewRequestsPage />;
    }

    // Other pages require authentication
    if (!isAuthenticated) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Please log in to access this area.</p>
        </div>
      );
    }

    // Check access for each page
    switch (activePage) {
      case "calendar":
        if (!hasAccess('calendar', 'view')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need calendar view access.</div>;
        }
        return <CalendarPage />;
      case "manage-calendar":
        if (!hasAccess('calendar-manage', 'view')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need calendar management access.</div>;
        }
        return <ManageCalendarPage canEdit={hasAccess('calendar-manage', 'edit')} />;
      case "workboards":
        // Check if user has access to any board
        if (!hasAnyBoardAccess()) {
          return <div style={{ padding: '2rem' }}>Access denied. You need board access.</div>;
        }
        // WorkboardsPage will filter boards based on user's specific access
        return <WorkboardsPage canEdit={hasAccess('board', 'edit')} />;
      case "editor":
        if (!hasAccess('admin', 'view')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need admin access.</div>;
        }
        return <EditorPage canEdit={hasAccess('admin', 'edit')} />;
      case "user-permissions":
        if (!hasAccess('admin', 'edit')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need edit access to the admin resource to manage user permissions.</div>;
        }
        return <UserPermissionsPage />;
      case "reporting":
        if (!hasAccess('admin', 'view')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need admin access to view reports.</div>;
        }
        return <ReportingPage />;
      case "teams-boards":
        if (!hasAccess('admin', 'edit')) {
          return <div style={{ padding: '2rem' }}>Access denied. You need edit access to the admin resource to manage teams and boards.</div>;
        }
        return <TeamsAndBoardsPage />;
      default:
        return <NewRequestsPage />;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="release-admin-page">
      {/* Show password setup modal if needed - blocks all access */}
      {isAuthenticated && needsPasswordSetup && (
        <SetPasswordModal 
          isOpen={true} 
          onClose={() => {}} // Cannot close - must set password
        />
      )}
      
      <SidePanel 
        setActivePage={handlePageChange}
        isAuthenticated={isAuthenticated}
        hasAccess={hasAccess}
        hasAnyBoardAccess={hasAnyBoardAccess}
        loading={loading}
      />
      <main className="admin-content">{renderPage()}</main>
    </div>
  );
};

export default ReleaseAdminPage;
